import React, { useEffect, useRef } from 'react'
import { AVAILABILITY_INFO } from '../../constants/availabilityMessages.js'
import './LeagueExplanationModal.css'

/**
 * LeagueExplanationModal - First-time visitor modal explaining ServeSync's unique approach
 *
 * Shows on first visit to league home page to explain the difference between
 * traditional flex leagues and ServeSync's flexible scheduling system.
 *
 * Props:
 * - isOpen: boolean - controls modal visibility
 * - onClose: function - called when user dismisses modal
 * - ctaButtonText: string - optional custom text for the CTA button (default: "Got it! Let's play 🎾")
 */
export default function LeagueExplanationModal({
  isOpen,
  onClose,
  ctaButtonText = "Got it! Let's play 🎾",
}) {
  const closeButtonRef = useRef(null)

  // Focus management: move focus to close button when modal opens
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [isOpen])

  // Escape key handler: allow keyboard dismissal
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className="league-explanation-backdrop" onClick={handleBackdropClick}>
      <div
        className="league-explanation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="league-explanation-title"
      >
        {/* Header */}
        <div className="lem-header">
          <div className="lem-header-content">
            <div className="lem-tennis-icon" aria-hidden="true">
              🎾
            </div>
            <h2 id="league-explanation-title" className="lem-title">
              Welcome to ServeSync Tennis!
            </h2>
            <p className="lem-subtitle">
              Here's what makes us different from traditional flex leagues
            </p>
          </div>
          <button
            ref={closeButtonRef}
            className="lem-close"
            onClick={onClose}
            aria-label="Close explanation"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="lem-body">
          {/* Traditional vs ServeSync Comparison */}
          <div className="lem-comparison">
            <div className="lem-comparison-card lem-traditional">
              <div className="lem-card-header">
                <span className="lem-card-icon" aria-hidden="true">
                  📅
                </span>
                <h3 className="lem-card-title">Traditional Flex League</h3>
              </div>
              <ul className="lem-feature-list">
                <li className="lem-feature-item">
                  <span className="lem-feature-icon" aria-hidden="true">
                    👥
                  </span>
                  <span className="lem-feature-text">
                    6 players in division = 5 opponents
                  </span>
                </li>
                <li className="lem-feature-item">
                  <span className="lem-feature-icon" aria-hidden="true">
                    📆
                  </span>
                  <span className="lem-feature-text">
                    5 weeks = 1 opponent per week
                  </span>
                </li>
                <li className="lem-feature-item">
                  <span className="lem-feature-icon" aria-hidden="true">
                    🔒
                  </span>
                  <span className="lem-feature-text">
                    Schedule directly with <strong>each specific</strong>{' '}
                    opponent
                  </span>
                </li>
                <li className="lem-feature-item">
                  <span className="lem-feature-icon" aria-hidden="true">
                    ⏰
                  </span>
                  <span className="lem-feature-text">
                    Fixed weekly matchups
                  </span>
                </li>
              </ul>
            </div>

            <div className="lem-comparison-arrow" aria-hidden="true">
              <span className="lem-arrow-icon">→</span>
            </div>

            <div className="lem-comparison-card lem-servesync">
              <div className="lem-card-header">
                <span className="lem-card-icon" aria-hidden="true">
                  ⚡
                </span>
                <h3 className="lem-card-title">ServeSync Tennis</h3>
              </div>
              <ul className="lem-feature-list">
                <li className="lem-feature-item lem-highlight">
                  <span className="lem-feature-icon" aria-hidden="true">
                    👥
                  </span>
                  <span className="lem-feature-text">
                    6 players in division = 5 opponents
                  </span>
                </li>
                <li className="lem-feature-item lem-highlight">
                  <span className="lem-feature-icon" aria-hidden="true">
                    🗓️
                  </span>
                  <span className="lem-feature-text">
                    5 weeks = Play <strong>any opponent at any time</strong>
                  </span>
                </li>
                <li className="lem-feature-item lem-highlight">
                  <span className="lem-feature-icon" aria-hidden="true">
                    🌐
                  </span>
                  <span className="lem-feature-text">
                    Set availability <strong>across all opponents</strong> (
                    {AVAILABILITY_INFO.SHORT_DESCRIPTION})
                  </span>
                </li>
                <li className="lem-feature-item lem-highlight">
                  <span className="lem-feature-icon" aria-hidden="true">
                    👀
                  </span>
                  <span className="lem-feature-text">
                    View availability <strong>across all opponents</strong>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="lem-benefits">
            <h3 className="lem-benefits-title">What This Means For You</h3>
            <div className="lem-benefits-grid">
              <div className="lem-benefit-card">
                <div className="lem-benefit-icon" aria-hidden="true">
                  📱
                </div>
                <div className="lem-benefit-content">
                  <h4 className="lem-benefit-heading">Maximum Flexibility</h4>
                  <p className="lem-benefit-text">
                    Play matches on your schedule - no rigid weekly assignments
                  </p>
                </div>
              </div>
              <div className="lem-benefit-card">
                <div className="lem-benefit-icon" aria-hidden="true">
                  🤝
                </div>
                <div className="lem-benefit-content">
                  <h4 className="lem-benefit-heading">Easy Coordination</h4>
                  <p className="lem-benefit-text">
                    See everyone's availability at once and find times that work
                  </p>
                </div>
              </div>
              <div className="lem-benefit-card">
                <div className="lem-benefit-icon" aria-hidden="true">
                  ⚡
                </div>
                <div className="lem-benefit-content">
                  <h4 className="lem-benefit-heading">Play More Tennis</h4>
                  <p className="lem-benefit-text">
                    Less time coordinating, more time on the court
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="lem-footer">
          <button className="lem-cta-button" onClick={onClose}>
            {ctaButtonText}
          </button>
        </div>
      </div>
    </div>
  )
}
